/*
    AssessmentDetails.tsx -- Displays the history of an assessment.
*/
import { Fragment, type JSX, useMemo } from 'react';

import { Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { Role } from 'Components/Context/RBACContext';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { ICON_EDIT_MODIFY_UPDATE } from 'Config/Icons';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { Assessment, AssessmentHistory, AssessmentRole, AssessmentState, OperationalControl, determineUserAssessmentRoles } from 'Models/OperationalControls';

import styles from './AssessmentDetails.module.css';

/** Get the most recent Control Owner comments.  */
const getLatestOwnerCommentString = (assessmentHistory: AssessmentHistory): string | undefined => {
    const owner_comments = assessmentHistory.assessment.owner_comments;
    if (!owner_comments || owner_comments.length === 0) {
        return;
    }
    return owner_comments[owner_comments.length - 1].text;
};

/** Get the timestamp of the most recent Control Owner comments.  */
const getLatestSubmittedTimestamp = (assessmentHistory: AssessmentHistory): string | undefined => {
    const owner_comments = assessmentHistory.assessment.owner_comments;
    if (!owner_comments || owner_comments.length === 0) {
        return;
    }
    return owner_comments[owner_comments.length - 1].timestamp;
};

/** Get the user ID of the most recent Control Owner comments. */
const getLatestOwnerUserId = (assessmentHistory: AssessmentHistory): string | undefined => {
    const owner_comments = assessmentHistory.assessment.owner_comments;
    if (!owner_comments || owner_comments.length === 0) {
        return;
    }
    return owner_comments[owner_comments.length - 1].user_id;
};

export interface AssessmentDetailsProps {
    detailedControlResponse: OperationalControl;
    assessment?: Assessment;
    assessmentDetailsError?: string;
    assessmentHistoryList?: AssessmentHistory[];
    assessmentLinkData: { to: string; state: Record<string, any> };
    authenticatedUserSubject?: string;
    authenticatedUserRoles: Role[];
}

export const AssessmentDetails = (props: AssessmentDetailsProps): JSX.Element => {
    const cachedData = useCachedData();

    const userAssessmentRoles = useMemo(() => determineUserAssessmentRoles(props.authenticatedUserSubject, props.authenticatedUserRoles, props.detailedControlResponse.configuration.review_configuration?.owner_subject, props.detailedControlResponse.configuration.review_configuration?.reviewer_subject), [props.authenticatedUserSubject, props.authenticatedUserRoles, props.detailedControlResponse]);

    const columns: ColumnConfig<AssessmentHistory>[] = [
        {
            type: ColumnType.DATE,
            name: 'Completed',
            getDateString: (data) => data.timestamp,
        },
        {
            type: ColumnType.DATE,
            name: 'Due',
            getDateString: (data) => data.assessment.assessment_due_date,
        },
        {
            type: ColumnType.PRIMARY_AND_SECONDARY_STRING,
            name: 'Owner Comment',
            getPrimaryString: (data) => getLatestOwnerCommentString(data),
            getSecondaryString: (data) => {
                const timestamp = getLatestSubmittedTimestamp(data);
                const userId = getLatestOwnerUserId(data);
                if (timestamp && userId) {
                    return `${getUserNameFromSubject(userId, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)} ${iso8601ToUsDateShort(timestamp)}`;
                }
                return undefined;
            },
        },
        {
            type: ColumnType.PRIMARY_AND_SECONDARY_STRING,
            name: 'Reviewer Comment',
            getPrimaryString: (data) => data.assessment.reviewer_comment?.text,
            getSecondaryString: (data) => {
                const comment = data.assessment.reviewer_comment;
                if (comment?.timestamp && comment?.user_id) {
                    return `${getUserNameFromSubject(comment.user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)} ${iso8601ToUsDateShort(comment.timestamp)}`;
                }
                return undefined;
            },
        },
    ];

    /** Determine if the Assessment has been started. */
    const assessmentHasStarted = (): boolean => {
        if (props.assessment?.assessment_state === AssessmentState.UNDER_REVIEW) {
            return true;
        }
        return props.assessment?.assessment_state === AssessmentState.IN_PROGRESS && ((props.assessment.owner_comments && props.assessment.owner_comments.length > 0) || props.assessment.assessment_effectiveness !== undefined);
    };

    /** Render the Assessment button based on user roles and Assessment state. */
    const assessmentButton = (): JSX.Element | null => {
        const assessmentConfigured = props.assessment !== undefined;
        const userCanSubmitForReview = userAssessmentRoles.has(AssessmentRole.ADMIN) || userAssessmentRoles.has(AssessmentRole.OWNER);
        const userCanApprove = userAssessmentRoles.has(AssessmentRole.ADMIN) || userAssessmentRoles.has(AssessmentRole.REVIEWER);

        if (!assessmentConfigured) {
            return null;
        }

        let buttonText = 'Assessment';

        if (!assessmentHasStarted()) {
            if (userCanSubmitForReview) {
                buttonText = 'Begin Assessment';
            }
            return (
                <Link variant="primaryButton" to={props.assessmentLinkData.to} state={props.assessmentLinkData.state} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                    {buttonText}
                </Link>
            );
        }

        if (props.assessment?.assessment_state === AssessmentState.IN_PROGRESS) {
            return (
                <Link variant="primaryButton" to={props.assessmentLinkData.to} state={props.assessmentLinkData.state} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                    Assessment
                </Link>
            );
        }

        if (userCanApprove) {
            buttonText = 'Review Assessment';
        }

        return (
            <Link variant="primaryButton" to={props.assessmentLinkData.to} state={props.assessmentLinkData.state} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                {buttonText}
            </Link>
        );
    };

    return (
        <Fragment>
            <div className={styles.formHeaderContainer}>
                <Text color="blue" noStyles variant="Header2">
                    Assessment History
                </Text>
                {assessmentButton()}
            </div>
            {props.assessmentDetailsError && <Text color="red">{props.assessmentDetailsError}</Text>}
            <Table<AssessmentHistory> disableSorting columns={columns} defaultSortColumn="Completed" defaultSortDirection="descending" filters={[]} getUniqueIdForRow={(data) => data.timestamp} rowsData={props.assessmentHistoryList} />
        </Fragment>
    );
};
